/*
  Copyright (C) 2024 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
*/

import { ref, shallowRef, readonly, onUnmounted } from "vue";

/**
 * Default configuration for WebSocket connection
 */
const DEFAULT_CONFIG = {
  reconnectInterval: 1000,
  maxReconnectInterval: 30000,
  reconnectDecay: 1.5,
  maxReconnectAttempts: 10,
  heartbeatInterval: 30000,
  messageBufferSize: 100,
};

/**
 * Connection states
 */
export const ConnectionState = {
  DISCONNECTED: "disconnected",
  CONNECTING: "connecting",
  CONNECTED: "connected",
  RECONNECTING: "reconnecting",
  ERROR: "error",
};

/**
 * Vue composable for managing WebSocket connections for collaborative editing.
 * Provides automatic reconnection, heartbeat, and message buffering.
 *
 * @param {Object} config - Configuration options
 * @returns {Object} Composable API
 */
export function useCollabWebSocket(config = {}) {
  const options = { ...DEFAULT_CONFIG, ...config };

  // Reactive state
  const connectionState = ref(ConnectionState.DISCONNECTED);
  const lastError = ref(null);
  const lastCloseCode = ref(null);
  const reconnectAttempts = ref(0);
  const messageBuffer = shallowRef([]);

  // Internal state (non-reactive)
  let socket = null;
  let heartbeatTimer = null;
  let reconnectTimer = null;
  let currentUrl = null;
  let isClosedByUser = false;

  // Event handlers
  const handlers = {
    onConnected: [],
    onDisconnected: [],
    onMessage: [],
    onError: [],
    onReconnecting: [],
  };

  /**
   * Registers an event handler
   */
  const on = (event, handler) => {
    if (handlers[event]) {
      handlers[event].push(handler);
    }
    return () => off(event, handler);
  };

  /**
   * Removes an event handler
   */
  const off = (event, handler) => {
    if (handlers[event]) {
      const index = handlers[event].indexOf(handler);
      if (index > -1) {
        handlers[event].splice(index, 1);
      }
    }
  };

  /**
   * Emits an event to all registered handlers
   */
  const emit = (event, ...args) => {
    if (handlers[event]) {
      handlers[event].forEach((handler) => handler(...args));
    }
  };

  /**
   * Connects to the WebSocket server
   *
   * @param {string} url - WebSocket URL
   * @returns {Promise<void>}
   */
  const connect = (url) => {
    return new Promise((resolve, reject) => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      currentUrl = url;
      isClosedByUser = false;
      connectionState.value = ConnectionState.CONNECTING;

      try {
        socket = new WebSocket(url);

        socket.onopen = () => {
          connectionState.value = ConnectionState.CONNECTED;
          reconnectAttempts.value = 0;
          lastError.value = null;
          startHeartbeat();
          flushMessageBuffer();
          emit("onConnected");
          resolve();
        };

        socket.onclose = (event) => {
          stopHeartbeat();
          lastCloseCode.value = event.code;
          connectionState.value = ConnectionState.DISCONNECTED;

          // Permanent closes — retrying would hit the same wall, so go straight to error:
          //   1009: message too large.
          //   1008: policy violation / authentication timeout (expired or invalid ticket); the
          //         editor layer surfaces this as isAuthExpired and the user must reload.
          if (event.code === 1009 || event.code === 1008) {
            connectionState.value = ConnectionState.ERROR;
            emit("onDisconnected", event);
            return;
          }

          if (!isClosedByUser && !event.wasClean) {
            scheduleReconnect();
          }

          emit("onDisconnected", event);
        };

        socket.onerror = (error) => {
          lastError.value = error;
          connectionState.value = ConnectionState.ERROR;
          emit("onError", error);
          reject(error);
        };

        socket.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            handleMessage(message);
          } catch (e) {
            console.error("[CollabWebSocket] Failed to parse message:", e);
          }
        };
      } catch (error) {
        lastError.value = error;
        connectionState.value = ConnectionState.ERROR;
        emit("onError", error);
        reject(error);
      }
    });
  };

  /**
   * Disconnects from the WebSocket server
   */
  const disconnect = () => {
    isClosedByUser = true;
    stopHeartbeat();
    clearReconnectTimer();

    if (socket) {
      socket.close(1000, "User disconnected");
      socket = null;
    }

    connectionState.value = ConnectionState.DISCONNECTED;
    messageBuffer.value = [];
  };

  /**
   * Sends a message to the server
   *
   * @param {Object} message - Message to send
   * @returns {boolean} True if sent immediately, false if buffered
   */
  const send = (message) => {
    const json = JSON.stringify(message);

    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(json);
      return true;
    }

    // Buffer message for later
    if (messageBuffer.value.length < options.messageBufferSize) {
      messageBuffer.value = [...messageBuffer.value, json];
    } else {
      console.warn("[CollabWebSocket] Message buffer full, dropping message");
    }
    return false;
  };

  /**
   * Handles incoming messages
   */
  const handleMessage = (message) => {
    // Handle pong response
    if (message.type === "pong") {
      return;
    }

    emit("onMessage", message);
  };

  /**
   * Starts the heartbeat timer
   */
  const startHeartbeat = () => {
    stopHeartbeat();
    heartbeatTimer = setInterval(() => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        send({ type: "ping" });
      }
    }, options.heartbeatInterval);
  };

  /**
   * Stops the heartbeat timer
   */
  const stopHeartbeat = () => {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  };

  /**
   * Schedules a reconnection attempt
   */
  const scheduleReconnect = () => {
    if (reconnectAttempts.value >= options.maxReconnectAttempts) {
      console.error("[CollabWebSocket] Max reconnection attempts reached");
      connectionState.value = ConnectionState.ERROR;
      return;
    }

    connectionState.value = ConnectionState.RECONNECTING;
    reconnectAttempts.value++;

    const delay = Math.min(
      options.reconnectInterval *
        Math.pow(options.reconnectDecay, reconnectAttempts.value - 1),
      options.maxReconnectInterval,
    );

    emit("onReconnecting", reconnectAttempts.value, delay);

    reconnectTimer = setTimeout(() => {
      if (currentUrl && !isClosedByUser) {
        // Clear stale message buffer before reconnecting
        messageBuffer.value = [];
        connect(currentUrl).catch(() => {
          // Reconnect will be scheduled again by onclose handler
        });
      }
    }, delay);
  };

  /**
   * Clears the reconnect timer
   */
  const clearReconnectTimer = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  /**
   * Flushes the message buffer
   */
  const flushMessageBuffer = () => {
    if (
      socket &&
      socket.readyState === WebSocket.OPEN &&
      messageBuffer.value.length > 0
    ) {
      const messages = [...messageBuffer.value];
      messageBuffer.value = [];

      messages.forEach((json) => {
        socket.send(json);
      });
    }
  };

  /**
   * Returns whether the connection is currently active
   */
  const isConnected = () => {
    return socket && socket.readyState === WebSocket.OPEN;
  };

  /**
   * Resets the reconnect attempt counter (for manual retry)
   */
  const resetReconnectAttempts = () => {
    reconnectAttempts.value = 0;
  };

  // Cleanup on unmount
  onUnmounted(() => {
    disconnect();
  });

  return {
    // State (readonly)
    connectionState: readonly(connectionState),
    lastError: readonly(lastError),
    lastCloseCode: readonly(lastCloseCode),
    reconnectAttempts: readonly(reconnectAttempts),
    bufferedMessages: readonly(messageBuffer),

    // Methods
    connect,
    disconnect,
    send,
    isConnected,
    resetReconnectAttempts,

    // Event handlers
    on,
    off,
  };
}

export default useCollabWebSocket;
